import { apiBase, isV2, terminalId } from "./config";
import { ApiError, getBranch } from "./api";
import { downloadPath } from "./v2gateway";

function token(): string | null {
  try {
    return localStorage.getItem("astro.token");
  } catch {
    return null;
  }
}

function filenameFrom(res: Response, fallback: string): string {
  const cd = res.headers.get("content-disposition") ?? "";
  const m = /filename="?([^";]+)"?/i.exec(cd);
  return m?.[1] ?? fallback;
}

/**
 * Downloads a file the API generates (PDF / XLSX / CSV). Uses fetch so the auth header is sent,
 * then triggers a normal browser save. API errors surface as ApiError for the toast layer.
 */
export async function downloadFile(path: string, opts: { method?: "GET" | "POST"; body?: unknown; fallbackName: string }): Promise<string> {
  let res: Response;
  try {
    res = await fetch(`${apiBase()}${isV2() ? downloadPath(path) : path}`, {
      method: opts.method ?? "GET",
      headers: {
        "content-type": "application/json",
        "x-terminal-id": terminalId(),
        ...(token() ? { authorization: `Bearer ${token()}` } : {}),
        ...(getBranch() ? { "x-branch-id": getBranch()! } : {}),
      },
      body: opts.body === undefined ? undefined : JSON.stringify(opts.body),
    });
  } catch {
    throw new ApiError(0, "NETWORK_ERROR", "Cannot reach the server");
  }
  if (!res.ok) {
    const j = (await res.json().catch(() => ({}))) as { error?: { code?: string; message?: string } };
    throw new ApiError(res.status, j.error?.code ?? "HTTP_ERROR", j.error?.message ?? res.statusText);
  }
  const name = filenameFrom(res, opts.fallbackName);
  const url = URL.createObjectURL(await res.blob());
  const a = document.createElement("a");
  a.href = url;
  a.download = name;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 10_000);
  return name;
}

/** The printable page of a document (`?format=html`), fetched with the session. Used for silent printing in the Electron POS. */
export async function fetchDocumentHtml(path: string): Promise<string> {
  const sep = path.includes("?") ? "&" : "?";
  let res: Response;
  try {
    res = await fetch(`${apiBase()}${isV2() ? downloadPath(path) : path}${sep}format=html`, {
      headers: { "x-terminal-id": terminalId(), ...(token() ? { authorization: `Bearer ${token()}` } : {}), ...(getBranch() ? { "x-branch-id": getBranch()! } : {}) },
    });
  } catch {
    throw new ApiError(0, "NETWORK_ERROR", "Cannot reach the server");
  }
  if (!res.ok) {
    const j = (await res.json().catch(() => ({}))) as { error?: { code?: string; message?: string } };
    throw new ApiError(res.status, j.error?.code ?? "HTTP_ERROR", j.error?.message ?? res.statusText);
  }
  return res.text();
}
